var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isEmptyFilter } from '../../FiltersBar/utils';
export function createUseDataTreeFunction(hookConfig) {
    const hookStateValues = Object.values(hookConfig);
    const needUrl = hookStateValues.includes('url');
    const needHistory = hookStateValues.includes('history');
    const needState = hookStateValues.includes('state');
    const useHook = (params) => {
        let location = undefined;
        let navigate = undefined;
        let searchParams = undefined;
        if (needUrl || needHistory) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            location = useLocation();
            // eslint-disable-next-line react-hooks/rules-of-hooks
            navigate = useNavigate();
            if (needUrl) {
                searchParams = new URLSearchParams(location.search);
            }
        }
        let internalState = undefined;
        let setInternalState = undefined;
        if (needState) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            [internalState, setInternalState] = React.useState(() => {
                const initialFilterData = params.initialFilterData && hookConfig.filterState === 'state';
                const initialSelectedItemIds = params.initialSelectedItemIds && hookConfig.selectionState === 'state';
                if (initialFilterData || initialSelectedItemIds) {
                    const initialState = {};
                    if (initialFilterData) {
                        initialState.filter = params.initialFilterData;
                    }
                    if (initialSelectedItemIds) {
                        initialState.selectedItemIds = params.initialSelectedItemIds;
                    }
                    return initialState;
                }
                else {
                    return emptyObject;
                }
            });
        }
        const changeUrl = React.useCallback((changeSearchParams) => {
            var _a, _b;
            const newSearchParams = new URLSearchParams(location === null || location === void 0 ? void 0 : location.search);
            const newState = changeSearchParams(newSearchParams, (_a = location === null || location === void 0 ? void 0 : location.state) !== null && _a !== void 0 ? _a : emptyObject);
            navigate === null || navigate === void 0 ? void 0 : navigate({ pathname: '.', search: newSearchParams.toString() }, { state: Object.assign(Object.assign({}, (newState ? newState : ((_b = location === null || location === void 0 ? void 0 : location.state) !== null && _b !== void 0 ? _b : emptyObject))), { internal: true }), replace: true });
        }, [location === null || location === void 0 ? void 0 : location.search, location === null || location === void 0 ? void 0 : location.state, navigate]);
        const getHistoryOrState = (state) => {
            var _a;
            return state === 'history' ? ((_a = location === null || location === void 0 ? void 0 : location.state) !== null && _a !== void 0 ? _a : emptyObject) : internalState;
        };
        const changeHistoryOrState = React.useCallback((state, changeState) => {
            var _a;
            switch (state) {
                case 'history':
                    navigate === null || navigate === void 0 ? void 0 : navigate({ pathname: '.', search: location === null || location === void 0 ? void 0 : location.search }, {
                        state: typeof changeState === 'function'
                            ? changeState((_a = location === null || location === void 0 ? void 0 : location.state) !== null && _a !== void 0 ? _a : emptyObject)
                            : Object.assign(Object.assign({}, location === null || location === void 0 ? void 0 : location.state), changeState),
                        replace: true,
                    });
                    break;
                case 'state':
                    setInternalState(changeState);
                    break;
            }
        }, [location === null || location === void 0 ? void 0 : location.search, location === null || location === void 0 ? void 0 : location.state, navigate, setInternalState]);
        return {
            useFilterState: () => {
                var _a, _b, _c;
                if (hookConfig.filterState === 'url') {
                    const filterParam = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('filter');
                    let filterData;
                    if (filterParam !== 'state') {
                        filterData = filterParam ? JSON.parse(filterParam) : emptyObject;
                    }
                    else {
                        const state = getHistoryOrState('history');
                        filterData = (_a = state === null || state === void 0 ? void 0 : state.filter) !== null && _a !== void 0 ? _a : emptyObject;
                    }
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const initialFilterData = React.useRef(filterData);
                    if (!((_b = location === null || location === void 0 ? void 0 : location.state) === null || _b === void 0 ? void 0 : _b.internal)) {
                        initialFilterData.current = filterData;
                    }
                    return { filterData, initialFilterData: initialFilterData.current };
                }
                else {
                    const state = getHistoryOrState(hookConfig.filterState);
                    const filterData = (_c = state === null || state === void 0 ? void 0 : state.filter) !== null && _c !== void 0 ? _c : emptyObject;
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const initialFilterData = React.useRef(filterData);
                    return { filterData, initialFilterData: initialFilterData.current };
                }
            },
            useActiveState: () => {
                var _a;
                if (hookConfig.activeState === 'url') {
                    const activeItemId = (_a = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('id')) !== null && _a !== void 0 ? _a : undefined;
                    return { activeItemId };
                }
                else {
                    const state = getHistoryOrState(hookConfig.activeState);
                    const activeItemId = state === null || state === void 0 ? void 0 : state.id;
                    return { activeItemId };
                }
            },
            useSelectionState: () => {
                const state = getHistoryOrState(hookConfig.selectionState);
                const selectedItemIds = Array.isArray(state === null || state === void 0 ? void 0 : state.selectedItemIds) ? state === null || state === void 0 ? void 0 : state.selectedItemIds : emptyArray;
                return { selectedItemIds };
            },
            handleFilter: React.useCallback((newFilterData) => {
                const emptyFilter = isEmptyFilter(newFilterData);
                const changes = {
                    url: {
                        filter: false,
                    },
                    history: {
                        filter: false,
                    },
                    state: {
                        filter: false,
                    },
                };
                const collectChanges = (state, type) => {
                    changes[state][type] = true;
                };
                collectChanges(hookConfig.filterState, 'filter');
                if (changes.url.filter) {
                    changeUrl((newSearchParam, prevState) => {
                        const newState = Object.assign({}, prevState);
                        let returnNewState = false;
                        if (changes.url.filter) {
                            if (emptyFilter) {
                                newSearchParam.delete('filter');
                            }
                            else {
                                newSearchParam.set('filter', JSON.stringify(newFilterData));
                            }
                            changes.url.filter = false;
                        }
                        else if (changes.history.filter) {
                            if (emptyFilter) {
                                delete newState.filter;
                            }
                            else {
                                newState.filter = newFilterData;
                            }
                            returnNewState = true;
                            changes.history.filter = false;
                        }
                        if (params.maxUrlSearchLength != null && newSearchParam.toString().length > params.maxUrlSearchLength) {
                            newSearchParam.set('filter', 'state');
                            newState.filter = newFilterData;
                            returnNewState = true;
                        }
                        return returnNewState ? newState : undefined;
                    });
                }
                if (changes.history.filter) {
                    const history = {};
                    if (changes.history.filter) {
                        history.filter = newFilterData;
                        changes.history.filter = false;
                    }
                    changeHistoryOrState('history', history);
                }
                if (changes.state.filter) {
                    changes.state.filter = false;
                    changeHistoryOrState('state', (prevState) => (Object.assign(Object.assign({}, prevState), { filter: newFilterData })));
                }
            }, [changeUrl, changeHistoryOrState, params.maxUrlSearchLength]),
            setActiveItemId: React.useCallback((id) => {
                if (hookConfig.activeState === 'url') {
                    changeUrl((newSearchParam) => {
                        if (id) {
                            newSearchParam.set('id', id);
                        }
                        else {
                            newSearchParam.delete('id');
                        }
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.activeState, (prevState) => (Object.assign(Object.assign({}, prevState), { id: id })));
                }
            }, [changeUrl, changeHistoryOrState]),
            setSelectedItemIds: React.useCallback((changeState) => {
                let action;
                if (typeof changeState === 'function') {
                    action = (prevState) => {
                        var _a;
                        const selectedItemIds = (_a = prevState === null || prevState === void 0 ? void 0 : prevState.selectedItemIds) !== null && _a !== void 0 ? _a : [];
                        const newItemList = changeState(selectedItemIds);
                        return Object.assign(Object.assign({}, prevState), { selectedItemIds: newItemList });
                    };
                }
                else {
                    action = (prevState) => (Object.assign(Object.assign({}, prevState), changeState));
                }
                changeHistoryOrState(hookConfig.selectionState, action);
            }, [changeHistoryOrState]),
        };
    };
    return (params) => {
        var _a;
        const _b = useHook(params), { useFilterState, useActiveState, useSelectionState } = _b, handlers = __rest(_b, ["useFilterState", "useActiveState", "useSelectionState"]);
        const { filterData, initialFilterData } = useFilterState();
        const { activeItemId } = useActiveState();
        const { selectedItemIds } = useSelectionState();
        const [focusItemId, setFocusItemId] = React.useState();
        const [expandedItemIds, setExpandedItemIds] = React.useState();
        // Load data from backend
        const { data, isLoading, reload, customBackendResponseData } = params.useDataProvider({
            filterData,
        });
        React.useEffect(() => {
            if (data === null || data === void 0 ? void 0 : data.highlightedItemIds) {
                setExpandedItemIds(expandTreeItemsToShowSelectedItems(data.items, data.highlightedItemIds));
            }
        }, [data]);
        return {
            dataTreeProps: {
                loading: !!isLoading,
                items: (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : emptyArray,
                focusItemId: focusItemId,
                onChangeFocusItemId: setFocusItemId,
                activeItemId: activeItemId,
                onChangeActiveItemId: handlers.setActiveItemId,
                expandedItemIds: expandedItemIds,
                onChangeExpandedItemIds: setExpandedItemIds,
                selectedItemIds: selectedItemIds,
                onChangeSelectedItemIds: handlers.setSelectedItemIds,
                highlightedItemIds: data === null || data === void 0 ? void 0 : data.highlightedItemIds,
                customBackendResponseData: customBackendResponseData,
            },
            filtersBarProps: {
                initialFilterData: initialFilterData,
                onFilter: handlers.handleFilter,
            },
            appliedFilterData: filterData,
            reload: reload,
        };
    };
}
const _useDataTreeUsingUrl = createUseDataTreeFunction({
    filterState: 'url',
    activeState: 'url',
    selectionState: 'history',
});
const _useDataTreeUsingHistory = createUseDataTreeFunction({
    filterState: 'history',
    activeState: 'history',
    selectionState: 'history',
});
export const _useDataTreeUsingState = createUseDataTreeFunction({
    filterState: 'state',
    activeState: 'state',
    selectionState: 'state',
});
export function useDataTreeUsingUrl(params) {
    return _useDataTreeUsingUrl(params);
}
export function useDataTreeUsingHistory(params) {
    return _useDataTreeUsingHistory(params);
}
export function useDataTreeUsingState(params) {
    return _useDataTreeUsingState(params);
}
const emptyArray = [];
const emptyObject = {};
export function expandTreeItemsToShowSelectedItems(treeItems, selectedItemIds) {
    if (selectedItemIds && selectedItemIds.length > 0) {
        const expandedItemIds = [];
        collectExpandedTreeItemsToShowSelectedItems(treeItems, expandedItemIds, selectedItemIds);
        return expandedItemIds;
    }
    else {
        return [];
    }
}
function collectExpandedTreeItemsToShowSelectedItems(treeItems, expandedItemIds, selectedItemIds) {
    let expandParent = false;
    for (const item of treeItems) {
        if (item.children) {
            if (collectExpandedTreeItemsToShowSelectedItems(item.children, expandedItemIds, selectedItemIds)) {
                if (!expandedItemIds.includes(item.id)) {
                    expandedItemIds.push(item.id);
                }
                expandParent = true;
            }
        }
        if (selectedItemIds.includes(item.id)) {
            expandParent = true;
        }
    }
    return expandParent;
}
